import { css } from "@emotion/react";
import { isDate, isEqual, parse } from "date-fns";
import { FC, memo, useCallback } from "react";

import {
  DatePicker,
  IPickerListItem,
  NumericInput,
  Picker,
  RangeDatePicker,
  SpacingToken,
} from "@livechat/design-system-react-components";
import { IRangeDatePickerOption } from "@livechat/design-system-react-components/dist/components/DatePicker/types";

import Flex from "components/layout/Flex";
import { Filter } from "constants/filters";
import { CustomDateRangeFilterId, IDateRangeWithCompareFilter } from "constants/filters/date-range-filter";
import { withVar } from "themes/helpers";

import { dateRangeOptions, getItems, isCustomPeriodOptionSelected } from "./date-range-filter";
import { ICustomRangeDatePickerOption } from "./interfaces";

const timeFrameItems = [
  { key: "N", name: "Minute" },
  { key: "H", name: "Hour" },
  { key: "D", name: "Day" },
];

const timeFrameItemsPlural = [
  { key: "N", name: "Minutes" },
  { key: "H", name: "Hours" },
  { key: "D", name: "Days" },
];

interface IProps {
  filterName: Filter;
  filterValues?: IDateRangeWithCompareFilter;
  isOpen: boolean;
  onChange: (option: ICustomRangeDatePickerOption) => void;
  onClose(): void;
  onHandleNumericvalue(numericValue: string): void;
  numericValue: string;
  timeFrame: string;
  onHandleTimeFrame(timeFrame: string): void;
  skipCustomPeriodOption?: boolean;
  setSelectedOption: (option: IPickerListItem[] | null) => void;
  selectedOption: IPickerListItem[] | null;
}

export const DateRangeFilterControls: FC<IProps> = memo(props => {
  const {
    filterValues,
    onChange,
    numericValue,
    onHandleNumericvalue,
    timeFrame,
    skipCustomPeriodOption,
    onHandleTimeFrame,
    selectedOption,
    setSelectedOption,
    filterName,
  } = props;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onHandleTimeFrameChange = (timeFrame: IPickerListItem[] | null) => {
    if (!timeFrame) {
      return;
    }
    onHandleTimeFrame(timeFrame[0].key);
  };

  const handleDateChange = useCallback(
    (option: IRangeDatePickerOption | null) => {
      if (
        option &&
        // @ts-expect-error TS(2345)
        !isEqual(option.value.from, filterValues?.from as Date) &&
        // @ts-expect-error TS(2345)
        !isEqual(option.value.to, filterValues?.to as Date)
      ) {
        const { value } = option;
        // @ts-expect-error TS(2339)
        const { from, to } = value;
        if (selectedOption) {
          const payload = {
            id: CustomDateRangeFilterId.CustomPeriod,
            name: filterName,
            value: { from, to },
          };
          // @ts-expect-error TS(2345)
          onChange(payload);
        }
      }
    },
    [selectedOption, filterName, onChange]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handlePickerChange = (option: IPickerListItem[] | null) => {
    setSelectedOption(option);
    const selectedOption = dateRangeOptions?.find(item => item.id === (option && option[0].key));
    if (selectedOption) {
      onChange(selectedOption);
    }
  };

  const TodayDate = new Date();
  TodayDate.setHours(0, 0, 0, 0);

  const selectedTimeFrame =
    numericValue === "1"
      ? timeFrameItems.find(frame => frame.key === timeFrame)
      : timeFrameItemsPlural.find(frame => frame.key === timeFrame);

  return (
    <>
      <Picker
        id="date-range-filter"
        hideClearButton
        openedOnInit={!filterValues || !isCustomPeriodOptionSelected(filterValues)}
        onSelect={handlePickerChange}
        selected={selectedOption}
        options={getItems(skipCustomPeriodOption)}
        searchDisabled
        css={{ marginBottom: withVar(SpacingToken.Spacing2) }}
      />
      {selectedOption && selectedOption[0].key === CustomDateRangeFilterId.CustomTime ? (
        <Flex flex-direction="row" height="40px" gap={withVar(SpacingToken.Spacing2)}>
          {/* @ts-expect-error TS(2322) */}
          <NumericInput min={1} width="300px" value={numericValue} onChange={onHandleNumericvalue} />
          <Picker
            selected={selectedTimeFrame ? [selectedTimeFrame] : []}
            onSelect={onHandleTimeFrameChange}
            id="timepicker"
            options={numericValue === "1" ? timeFrameItems : timeFrameItemsPlural}
            searchDisabled
            hideClearButton
          />
        </Flex>
      ) : null}

      <div
        // There's some misalignment on empty cells in the calendar if this fix is not applied
        css={css`
          * {
            vertical-align: middle;
          }
        `}
      >
        {selectedOption && selectedOption[0].key === CustomDateRangeFilterId.CustomPeriod ? (
          <RangeDatePicker
            initialSelectedItemKey="default"
            onChange={handleDateChange}
            toMonth={TodayDate}
            initialFromDate={isDate(filterValues?.from) ? parse(filterValues?.from as string) : undefined}
            initialToDate={isDate(filterValues?.to) ? parse(filterValues?.to as string) : undefined}
          >
            {({ datepicker }) => <DatePicker {...datepicker} />}
          </RangeDatePicker>
        ) : null}
      </div>
    </>
  );
});

DateRangeFilterControls.displayName = "DateRangeFilterControls";
